import { AISummary } from "@/components/ai/AISummary.tsx";
import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { PerformanceReviewParticipantElements } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewParticipantElements.tsx";
import { PerformanceReviewParticipantSectionsTable } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewParticipantSectionsTable.tsx";
import { PerformanceReviewReviewerTypeSummaries } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewReviewerTypeSummaries.tsx";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { PerformanceReviewParticipantShareSettingModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewParticipantShareSettingModal.tsx";
import { usePerformanceReviewParticipantReport } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import { PerformanceReviewReviewerTypeItems } from "@/types/performance-review.types.ts";
import {
  Avatar,
  Box,
  Button,
  EmptyStateBase,
  FontAwesomeIcon,
  FullScreenPage,
  Skeleton,
  Stack,
  TabItem,
  Tabs,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface ReviewerTypeSidebarItem {
  id: number | undefined;
  title: string;
}

export const PerformanceReviewParticipantReport = () => {
  const { id, employee_id, type_id, tab } = useParams();
  const basePath = `/performance-reviews/${id}/view/participants/${employee_id}`;
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal } = useModals();
  const { renderEmployeeName } = useFormatter();

  const currentReviewerTypeId = type_id ? parseInt(type_id) : undefined;

  const isOverallMode = currentReviewerTypeId == undefined;

  const performanceReviewId = id ? parseInt(id) : undefined;
  const { data: report, isLoading: isLoading } = usePerformanceReviewParticipantReport({
    id: performanceReviewId,
    employee_id: employee_id ? parseInt(employee_id) : undefined,
    filter: { reviewer_type_id: currentReviewerTypeId ? currentReviewerTypeId : undefined },
  });

  useTitle(
    report ? renderEmployeeName(report.report.employee) : undefined,
    report?.report.performance_review.title,
    t("performance_review:performance_reviews")
  );

  const handleOpenParticipantShareSetting = () => {
    if (!report?.share_setting) return;
    if (!report?.report.employee) return;
    if (!performanceReviewId) return;

    openModal(PerformanceReviewParticipantShareSettingModal, {
      performanceReviewId: performanceReviewId,
      employee: report.report.employee,
      shareSetting: report.share_setting,
    });
  };

  const reviewerTypeSidebarItems = useMemo(() => {
    if (!report) return;

    const sidebarItems = [
      {
        id: undefined,
        title: t("performance_review:overall"),
      },
    ] as ReviewerTypeSidebarItem[];
    report.config.reviewer_types.map((reviewer_type) => {
      sidebarItems.push({
        id: reviewer_type.id,
        title:
          PerformanceReviewReviewerTypeItems().find((item) => item.id == reviewer_type.type)
            ?.shortTitle ??
          reviewer_type.title ??
          "",
      } as ReviewerTypeSidebarItem);
    });

    return sidebarItems;
  }, [report, currentReviewerTypeId]);

  const handleReviewerTypeChanged = (reviewer_type_id: number | undefined) => {
    if (reviewer_type_id)
      navigate(basePath + `/type/${reviewer_type_id}`, { state: location.state });
    else navigate(basePath, { state: location.state });
  };
  const handleTabChanged = (tabIndex: number) => {
    const urls = [basePath, basePath + "/feedbacks"];
    navigate(urls[tabIndex], { state: location.state });
  };

  const handleExit = () => {
    if (location.state?.from) navigate(location.state.from);
    else navigate(`/my-feedback/requests`);
  };

  const currentReviewerTypeItem = useMemo(() => {
    return reviewerTypeSidebarItems?.find((item) => item.id === currentReviewerTypeId);
  }, [reviewerTypeSidebarItems, currentReviewerTypeId]);

  const aiBlock = useMemo(() => {
    const aiProcessingStatus = report?.report.ai_processing_status;

    if (!report || !aiProcessingStatus || aiProcessingStatus === ApiAIProcessingStatus.disabled) {
      return null;
    }

    if (aiProcessingStatus === ApiAIProcessingStatus.completed && report.report.ai_summary) {
      return <AISummary ai_summary={report.report.ai_summary} />;
    }

    if (
      ![ApiAIProcessingStatus.waiting, ApiAIProcessingStatus.evaluating].includes(
        aiProcessingStatus
      )
    ) {
      return null;
    }

    const isWaiting = aiProcessingStatus === ApiAIProcessingStatus.waiting;

    return (
      <Stack
        gap={"sm"}
        className={twMerge(
          "border rounded-xs p-4",
          isWaiting ? "border-dark/40 bg-dark/10" : "border-violet/40 bg-violet/10"
        )}
        style={{
          boxShadow: isWaiting
            ? `0px 16px 96px 0px color-mix(in srgb, var(--color-dark) 24%, transparent), 0px 4px 8px 0px hsla(0, 0%, 0%, 0.08)`
            : `0px 16px 96px 0px color-mix(in srgb, var(--color-violet) 24%, transparent), 0px 4px 8px 0px hsla(0, 0%, 0%, 0.08)`,
        }}
      >
        <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
          <Avatar
            emoji={isWaiting ? ":sleeping:" : ":brain:"}
            className={twMerge(isWaiting ? "bg-dark/60" : "bg-violet/60")}
          />
          <Text className={twMerge("font-semibold", isWaiting ? "text-dark" : "text-violet")}>
            {isWaiting
              ? t("performance_review:ai_waiting_title")
              : t("performance_review:ai_evaluating_title")}
          </Text>
        </Stack>
        <Text className={twMerge(isWaiting ? "text-primary" : "text-violet")}>
          {isWaiting
            ? t("performance_review:ai_waiting_description")
            : t("performance_review:ai_evaluating_description")}
        </Text>
      </Stack>
    );
  }, [report, t]);

  return (
    <FullScreenPage
      isCenterVertical={false}
      title={
        t("performance_review:performance_review") +
        (report
          ? ` · ${report.report.performance_review.title} · ${renderEmployeeName(report.report.employee)}`
          : "")
      }
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={handleExit}>
          {t("common:exit")}
        </Button>
      }
      sidebarSlot={
        <Stack className={"px-6"} gap={"md"}>
          {reviewerTypeSidebarItems &&
            reviewerTypeSidebarItems.map((typeItem) => {
              return (
                <Text
                  key={`item-${typeItem.id}`}
                  onClick={() => {
                    handleReviewerTypeChanged(typeItem.id);
                  }}
                  className={twMerge(
                    currentReviewerTypeItem?.id == typeItem.id ? "text-default" : "text-secondary",
                    "cursor-pointer"
                  )}
                >
                  {typeItem.title}
                </Text>
              );
            })}
          {isLoading && (
            <>
              <Skeleton className={"w-20 h-5"} />
              <Skeleton className={"w-20 h-5"} />
              <Skeleton className={"w-20 h-5"} />
            </>
          )}
        </Stack>
      }
      actionsSlot={
        report &&
        report.share_setting && (
          <Button
            onClick={handleOpenParticipantShareSetting}
            leftIcon={
              report.share_setting.is_shared ? (
                <FontAwesomeIcon icon={"fa-light fa-check"} />
              ) : undefined
            }
            variant={"primary"}
            size={"lg"}
          >
            {report.share_setting.is_shared
              ? t("performance_review:shared")
              : t("performance_review:share")}
          </Button>
        )
      }
    >
      <Stack gap={"2xl"}>
        {report && (
          <>
            <Stack gap={"md"}>
              <Title
                header={currentReviewerTypeItem?.title ?? ""}
                caption={t("common:of", {
                  current: report.report.summary.completed_count,
                  total: t("plurals:feedbacks_completed_count", {
                    count: report.report.summary.total_count,
                  }),
                })}
                size={"xl"}
              />
              <Box
                className={twMerge(
                  "h-1 rounded-xs bg-dark/10 w-full px-2 overflow-hidden relative"
                )}
              >
                <Box
                  style={{
                    width:
                      report.report.summary.total_count == 0
                        ? 0
                        : (report.report.summary.completed_count /
                            report.report.summary.total_count) *
                            100 +
                          "%",
                  }}
                  className={twMerge("absolute left-0 h-full rounded-xs bg-brand-dark")}
                />
              </Box>
            </Stack>
            {report.report.is_below_anonymity_threshold && (
              <EmptyStateBase
                emoji={":lock:"}
                title={t("performance_review:result_not_available_title")}
                description={t("performance_review:result_not_available_description")}
              />
            )}

            {!report.report.is_below_anonymity_threshold && aiBlock && <Box>{aiBlock}</Box>}

            {report.report.summary.rating.avg_scale_score && (
              <Stack gap={"md"}>
                <Stack direction={"horizontal"} gap={"md"} items={"center"}>
                  <ScoreTag score={report.report.summary.rating.avg_scale_score} size={"xl"} />
                  <Stack direction={"horizontal"}>
                    <Title header={t("performance_review:overall_score")} size={"xl"} />
                  </Stack>
                </Stack>
              </Stack>
            )}

            {!report.report.is_below_anonymity_threshold && (
              <>
                {isOverallMode ? (
                  <>
                    {report.report.sections_report && (
                      <PerformanceReviewParticipantSectionsTable
                        sectionsReport={report.report.sections_report}
                        editReviewerId={
                          report.my_response ? report.my_response.reviewer_id : undefined
                        }
                      />
                    )}
                    <Tabs
                      onActiveTabChange={handleTabChanged}
                      activeTab={tab == "feedbacks" ? 1 : 0}
                    >
                      <TabItem title={t("common:summary")}>
                        <Box className={"pt-10"}>
                          <PerformanceReviewParticipantElements
                            performanceReviewId={report.report.performance_review.id}
                            participantId={report.report.id}
                            reviewTypeId={currentReviewerTypeItem?.id}
                            elementsReport={report.report.elements_report}
                          />
                        </Box>
                      </TabItem>
                      <TabItem title={t("performance_review:personal_feedbacks")}>
                        <Box className={"pt-10"}>
                          <PerformanceReviewReviewerTypeSummaries
                            reviewerTypeSummaries={report.report.reviewer_type_summaries}
                          />
                        </Box>
                      </TabItem>
                    </Tabs>
                  </>
                ) : (
                  <Box>
                    <PerformanceReviewReviewerTypeSummaries
                      reviewerTypeSummaries={report.report.reviewer_type_summaries}
                    />
                    <Box className={"pt-10"}>
                      <PerformanceReviewParticipantElements
                        performanceReviewId={report.report.performance_review.id}
                        participantId={report.report.id}
                        reviewTypeId={currentReviewerTypeItem?.id}
                        elementsReport={report.report.elements_report}
                      />
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
        {isLoading && (
          <Stack gap={"2xl"}>
            <Stack gap={"md"}>
              <Skeleton className={"w-auto h-25"} />
              <Skeleton className={"w-auto h-30"} />
            </Stack>
            <Stack gap={"md"}>
              <Skeleton className={"w-auto h-50"} />
            </Stack>
            <Stack gap={"md"}>
              <Skeleton className={"w-auto h-30"} />
            </Stack>
          </Stack>
        )}
      </Stack>
      <Box className={"lg:fixed lg:pt-45 pt-10 right-0 top-0 pr-6 pb-4 cursor-pointer"}>
        {report && (
          <Stack
            gap={"smd"}
            items={"start"}
            className={"cursor-pointer"}
            onClick={() => {
              openModal(EmployeeViewModal, {
                employeeId: report.report.employee.id,
              });
            }}
          >
            <EmployeeAvatar employee={report.report.employee} size={"2xl"} />
            <Title
              header={t("scorecards:show_profile", {
                applicant: renderEmployeeName(report.report.employee),
              })}
              className={"max-w-40"}
            />
          </Stack>
        )}
        {isLoading && (
          <Stack gap={"smd"}>
            <Skeleton className={"w-41 h-41"} />
            <Skeleton className={"w-40 h-10"} />
          </Stack>
        )}
      </Box>
    </FullScreenPage>
  );
};
